import React, { useState, useEffect, useRef } from 'react';
import { Box, H1, Grid, Btn, BtnDot, Type, BtnText } from '@saladbob/sassafras'
import DropDown from '../DropDown';

import { Responsive } from '../../types'; 

const fonts = { default: 'scoreBoardSm', laptop: 'scoreBoard', hd: 'scoreBoardJumbo' };
const textStyle = { textShadow : '0 0 20px '};

type ScoreProps = {
    admin?: boolean,
    score?: number,
    status?: string,
    down?: () => void,
    up?: () => void,
    onInput?: (value?: number) => void,
};

type Props = {
    admin?: boolean,
    updateScore?: (score: number) => void,
    setScore?: (score: number) => void,
    startGame?: () => void,
    endGame?: () => void,
    patchForfeit?: (teamId: string) => void,
    changeTeam?: (teamKey: string) => void,
    patchStatus?: (status: string) => void,
    status?: string,
    team?: any,
    teamKey?: string,
    forfeit?: any,
    bye?: boolean,
    score?: number,
    winner?: any,
    color?: string,
    pt?: Responsive,
    pb?: Responsive,
    place?: string,
};

const filterOutNonDigits = (str) => {
    if (!str) {
        return 0;
    }
    const value = str.replace(/\D/g, '');
    return Number(value);
}

const numberKeyCodes = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'Backspace',
    'ArrowLeft',
    'ArrowRight',
];

const Score: React.FC<ScoreProps> = ({ score, admin, status, up, down, onInput }) => {
    const element = useRef(null);
    const handleInput = ({currentTarget}) => {
        if (typeof onInput === 'function') {
            onInput(filterOutNonDigits(currentTarget.innerHTML));
        }
    };

    return (
        <Box justify="center">
            {admin && status === 'live' && (
                <BtnText
                    color="yellow400"
                    padding={[0, 'xs']}
                    margin={[0, 'sm']}
                    onClick={down}
                >
                    <Type color="yellow400" font={fonts}>{'<'}</Type>
                </BtnText>
            )}
            <H1
                font={fonts}
                align="center"
                color="yellow400"
                mb="0"
                width="auto"
            >
                {admin && status === 'live' && (
                    <span
                        ref={element}
                        contentEditable="true"
                        onKeyDown={(e) => {
                            if (numberKeyCodes.includes(e.key)) {
                                return;
                            }
                            if (e.key === 'Enter') {
                                e.currentTarget.blur();
                            }
                            e.preventDefault();
                        }}
                        onBlur={handleInput}
                    >
                        {pad(score)}
                    </span>
                )}
                {(!admin || status !== 'live') && pad(score)}
            </H1>
            {admin && status === 'live' && (
                <BtnText
                    padding={[0, 'xs']}
                    margin={[0, 'sm']}
                    onClick={up}
                >
                    <Type color="yellow400" style={textStyle} font={fonts}>{'>'}</Type>
                </BtnText>
            )}
        </Box>
    );

    function pad(num) {
        num = num.toString();
        while (num.length < 2) num = "0" + num;
        return num;
    }
}

const ScoreBoard: React.FC<Props> = ({
    admin,
    updateScore,
    setScore,
    startGame,
    endGame,
    patchForfeit,
    patchStatus,
    changeTeam,
    status,
    team,
    teamKey,
    forfeit,
    bye,
    score,
    winner,
    color,
    pt,
    pb,
    place,
}) => {
    return (
        <Grid
            bgColor={color}
            ps="md"
            pe="md"
            pt={pt}
            pb={pb}
            txtColor="white"
            rows={{ phone: '64px minMax(max-content, 1fr) min-content', laptop: '64px 1fr 96px' }}
            columns="1fr"
        >
            <Box>
                {winner && (winner.id || winner) === team?.id && (
                    <H1
                        color="yellow"
                        padding="sm"
                        font={{ phone: 'leader', laptop: 'h3' }}
                        align="center"
                    >
                        WINNER
                    </H1>
                )}
                {bye && status === 'bye' && (
                    <H1
                        color="yellow"
                        padding="sm"
                        font={{ phone: 'leader', laptop: 'h3' }}
                        align="center"
                    >
                        BYE
                    </H1>
                )}
            </Box>
            <Box verticalAlign="center" align="center" pb="md">
                <H1 align="center">{team?.name || place}</H1>
                {!admin && (winner && (winner.id || winner) === team?.id) && (
                    <H1
                        font={fonts}
                        align="center"
                        color="yellow400"
                    >
                            WINNER
                    </H1>
                )}
                {!admin && bye && status === 'bye' && (
                    <H1
                        font={fonts}
                        align="center"
                        color="yellow400"
                    >
                        BYE
                    </H1>
                )}
                {!admin && (winner && (winner.id || winner) !== team?.id) && (
                    <H1
                        font={fonts}
                        align="center"
                        color="yellow400"
                    >
                        LOSER
                    </H1>
                )}
                {admin && (
                    <>
                        <Score
                            admin={admin}
                            score={score}
                            status={status}
                            down={() => updateScore(-1)}
                            up={() => updateScore(1)}
                            onInput={setScore}
                        />
                        <Box mt="md" width="min-content" style={{ position: 'relative', zIndex: 1 }}>
                            <DropDown
                                alignment="bc"
                                button={(<BtnDot color="white" outline txtColor="black"><Type font="small">&#x2022;&#x2022;&#x2022;</Type></BtnDot>)}
                            >
                                {(!bye && team && (!forfeit || forfeit.id !== team?.id)) && (
                                    <BtnText
                                        size="sm"
                                        txtColor="black"
                                        padding="sm"
                                        onClick={() => patchForfeit(team?.id)}
                                    >
                                        Forfeit
                                    </BtnText>
                                )}
                                {!bye && !team && forfeit && forfeit.id === team?.id && (
                                    <BtnText
                                        size="sm"
                                        txtColor="black"
                                        padding="sm" 
                                        onClick={() => patchForfeit(null)}
                                    >
                                        Unforfeit
                                    </BtnText>
                                )}
                                {bye && status !== 'bye' && (
                                    <BtnText
                                        size="sm"
                                        txtColor="black"
                                        padding="sm" 
                                        onClick={() => patchStatus('bye')}
                                    >
                                        Bye
                                    </BtnText>
                                )}
                                {bye && status === 'bye' && (
                                    <BtnText
                                        size="sm"
                                        txtColor="black"
                                        padding="sm"
                                        onClick={() => patchStatus('waiting')}
                                    >
                                        Remove Bye
                                    </BtnText>
                                )}
                                {changeTeam && (
                                    <BtnText
                                        size="sm"
                                        txtColor="black"
                                        padding="sm" 
                                        onClick={() => changeTeam(teamKey)}
                                    >
                                        {team ? 'Change Team' : 'Add Team'}
                                    </BtnText>
                                )}
                            </DropDown>
                        </Box>
                    </>
                )}
            </Box>
            <Box justify="flex-end" display={{ phone: 'none', laptop: 'flex' }}>
                {admin && startGame && (status === 'waiting' || status === 'ended') && (
                    <Btn size="lg" color="white" font="h3" mb="lg" outline onClick={startGame}>
                        <Type weight="bold">START GAME</Type>
                    </Btn>
                )}
                {admin && endGame && status === 'live' && (
                    <Btn size="lg" color="white" font="h3" mb="lg" me="auto" outline onClick={endGame}>
                        <Type weight="bold">END GAME</Type>
                    </Btn>
                )}
            </Box>
        </Grid>
    )
}

export default ScoreBoard;